<script setup lang="ts">
const { locale, t } = useI18n()
const {
  mode,
  booking,
  daysAgo,
  hasBookingWidget,
  repeatUrl,
  bookingsLink
} = useBookingWidget()

const hotel = computed(() => booking.value?.hotel)

const checkinDate = computed(() => booking.value?.date?.split('-').join('/') ?? '')
const checkinHour = computed(() =>
  booking.value?.hour ? formatDisplayHour(booking.value.hour, locale.value) : ''
)

const isUpcoming = computed(() => mode.value === 'upcoming')
const isRepeat = computed(() => mode.value === 'repeat')

const widgetTitle = computed(() => {
  if (isRepeat.value) return t('labels.proximaReserva.repetir')
  return t('labels.proximaReserva.titulo')
})

const widgetDescription = computed(() => {
  if (isRepeat.value) {
    if (daysAgo.value === 0) return t('labels.proximaReserva.repetirSubtituloHoy')
    if (daysAgo.value === 1) return t('labels.proximaReserva.repetirSubtituloAyer')
    if (daysAgo.value !== null) {
      return t('labels.proximaReserva.repetirSubtitulo', { days: daysAgo.value })
    }
    return ''
  }

  return [checkinHour.value, checkinDate.value].filter(Boolean).join(' · ')
})

const ctaLink = computed(() => {
  if (isRepeat.value) return repeatUrl.value ?? bookingsLink.value
  return bookingsLink.value
})

const ctaLabel = computed(() => {
  if (isRepeat.value) return t('buttons.repeatBook')
  return t('labels.proximaReserva.ver')
})
</script>

<template>
  <div v-if="hasBookingWidget && hotel" class="mt-6 px-5">
    <div
      class="flex flex-col sm:flex-row gap-4 sm:gap-6 items-stretch sm:items-center rounded-3xl border border-neutral-200 bg-white p-4 sm:p-5 shadow-xs"
    >
      <!-- Imagen -->
      <div class="relative h-40 sm:h-28 w-full sm:w-44 rounded-2xl overflow-hidden shrink-0">
        <NuxtImg
          provider="byhours"
          format="webp"
          loading="lazy"
          class="w-full h-full object-cover opacity-90"
          :alt="hotel.name"
          :src="generateImgUrl(hotel.image?.lightbox ?? '')"
        />
      </div>

      <!-- Información -->
      <div class="flex flex-col gap-1 min-w-0 flex-1">
        <div class="flex items-center gap-1.5">
          <span v-if="isUpcoming" class="relative flex h-2 w-2">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          <p v-if="isUpcoming" class="text-xs font-medium uppercase tracking-wide text-neutral-500">
            {{ widgetTitle }}
          </p>
        </div>

        <p class="font-bold text-lg truncate">{{ hotel.name }}</p>
        <p v-if="hotel.city_name" class="text-sm text-neutral-500 truncate">{{ hotel.city_name }}</p>

        <div v-if="widgetDescription" class="flex items-center gap-1.5 text-sm text-neutral-700 mt-0.5">
          <UIcon
            :name="isUpcoming ? 'i-lucide-calendar-clock' : 'i-lucide-history'"
            class="size-4 text-neutral-500 shrink-0"
          />
          <span class="truncate">{{ widgetDescription }}</span>
        </div>
      </div>

      <!-- CTA -->
      <div class="shrink-0">
        <UButton
          :to="ctaLink"
          :label="ctaLabel"
          color="primary"
          variant="soft"
          size="lg"
          class="w-full sm:w-auto justify-center"
        />
      </div>
    </div>
  </div>
</template>
